import React, { useEffect, Suspense } from 'react';
import {
    Routes,
    Route,
    Navigate,
    useLocation,
    useSearchParams
} from 'react-router-dom';
import { useAppSelector } from 'hooks/useStore';
import { DISABLE_TEST_REPORT } from 'global/constants';
import { auth, signOut, signInWithCustomToken } from 'config/firebase';
import Loader from 'components/Loader';
import Tests from 'pages/private/Tests';
// import SAT from 'pages/public/Tests/SAT';
// import PSAT from 'pages/public/Tests/PSAT';
// import ACT from 'pages/public/Tests/ACT';
import Profile from 'pages/private/Profile';
import DigitalSAT from 'pages/private/DigitalSAT';
import DigitalACT from 'pages/private/DigitalACT';
import DigitalSHSAT from 'pages/private/DigitalSHSAT';
import DigitalTSIA2 from 'pages/private/DigitalTSIA2';
import DigitalSATReport from 'pages/private/DigitalSAT/DigitalSATReport';
import DigitalSATReview from 'pages/private/DigitalSAT/DigitalSATReview';
import DigitalACTReport from 'pages/private/DigitalACT/DigitalACTReport';
import DigitalACTReview from 'pages/private/DigitalACT/DigitalACTReview';
import DigitalSHSATReport from 'pages/private/DigitalSHSAT/DigitalSHSATReport';
import DigitalSHSATReview from 'pages/private/DigitalSHSAT/DigitalSHSATReview';
import DigitalTSIA2Report from 'pages/private/DigitalTSIA2/DigitalTSIA2Report';
import DigitalTSIA2Review from 'pages/private/DigitalTSIA2/DigitalTSIA2Review';
import DigitalBCA from 'pages/private/DigitalBCA';
import DigitalBCAReport from 'pages/private/DigitalBCA/DigitalBCAReport';
import DigitalBCAReview from 'pages/private/DigitalBCA/DigitalBCAReview';
import PaperACT from 'pages/private/PaperACT';
import PaperACTReport from 'pages/private/PaperACT/PaperACTReport';
import Assignments from 'pages/private/Assignments';
import Assignment from 'pages/private/Assignment';
import AssignmentReport from 'pages/private/AssignmentReport';
import AssignmentReview from 'pages/private/AssignmentReview';
import Courses from 'pages/private/Courses';
import Lectures from 'pages/private/Lectures';
import Vocab from 'pages/private/Vocab';
import Flashcards from 'pages/private/Flashcards';
import VocabQuiz from 'pages/private/VocabQuiz';
import VocabQuizResult from 'pages/private/VocabQuizResult';
import FillApp from 'pages/private/FillApp';
import FillAppSection from 'pages/private/FillAppSection';
import Essays from 'pages/private/Essays';
import EssayEditor from 'pages/private/EssayEditor';
import NotFound from 'pages/error/NotFound';
import Unauthorized from 'pages/error/Unauthorized';
import ServerError from 'pages/error/ServerError';

function PrivateRoutes() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { status } = useAppSelector(state => state.error);
    const { organization } = useAppSelector(state => state.organization);
    const { user } = useAppSelector(state => state.user);

    const { state } = useLocation() as {
        state?: { referrer?: string; }
    };

    useEffect(() => {
        if (searchParams.get('auth_token')) {
            signOut(auth).then(() => {
                signInWithCustomToken(auth, searchParams.get('auth_token'))
                .catch(error => {
                    console.log(error);
                });

                setSearchParams({});
            });
        }
    }, []);

    if (status === 401) return <Unauthorized />;
    else if (status !== null) return <ServerError />;

    return (
        <Suspense fallback={<Loader />}>
            <Routes>
                <Route path='/' element={<Navigate to='/tests' />} />
                <Route path='/account' element={<Profile />} />
                <Route path='/tests' element={<Tests />} />
                {/* <Route path='/tests/digital-sat' element={<SAT />} />
                <Route path='/tests/digital-psat' element={<PSAT />} />
                <Route path='/tests/digital-act' element={<ACT />} /> */}
                <Route path='/test/digital-sat/:testId' element={<DigitalSAT />} />
                <Route path='/test/digital-act/:testId' element={<DigitalACT />} />
                <Route path='/test/digital-shsat/:testId' element={<DigitalSHSAT />} />
                <Route path='/test/digital-tsia2/:testId' element={<DigitalTSIA2 />} />
                <Route path='/test/digital-bca/:testId' element={<DigitalBCA />} />
                <Route path='/test/paper-act/:testId' element={<PaperACT />} />
                {!organization.customizations.find(customization => customization === 'Hide Vocab') && <>
                    <Route path='/vocab' element={<Vocab />} />
                    <Route path='/vocab/flashcards/:vocabSetId' element={<Flashcards />} />
                    <Route path='/vocab/quiz/result/:quizId' element={<VocabQuizResult />} />
                    <Route path='/vocab/quiz/:vocabSetId' element={<VocabQuiz />} />
                </>}
                <Route path='/assignments' element={<Assignments />} />
                <Route path='/assignments/report/:assignmentId' element={<AssignmentReport />} />
                <Route path='/assignments/review/:assignmentId' element={<AssignmentReview />} />
                <Route path='/assignment/:assignmentId' element={<Assignment />} />
                {!DISABLE_TEST_REPORT.includes(organization.alias) && <>
                    <Route path='/report/digital-sat/:testId' element={<DigitalSATReport />} />
                    <Route path='/review/digital-sat/:testId' element={<DigitalSATReview />} />
                    <Route path='/report/digital-act/:testId' element={<DigitalACTReport />} />
                    <Route path='/review/digital-act/:testId' element={<DigitalACTReview />} />
                    <Route path='/report/digital-shsat/:testId' element={<DigitalSHSATReport />} />
                    <Route path='/review/digital-shsat/:testId' element={<DigitalSHSATReview />} />
                    <Route path='/report/digital-tsia2/:testId' element={<DigitalTSIA2Report />} />
                    <Route path='/review/digital-tsia2/:testId' element={<DigitalTSIA2Review />} />
                    <Route path='/report/digital-bca/:testId' element={<DigitalBCAReport />} />
                    <Route path='/review/digital-bca/:testId' element={<DigitalBCAReview />} />
                    <Route path='/report/paper-act/:testId' element={<PaperACTReport />} />
                </>}
                {organization.customizations.find(customization => customization === 'Video Lectures') && <>
                    <Route path='/lectures' element={<Courses />} />
                    <Route path='/lectures/:courseUrl' element={<Lectures />} />
                </>}
                {/* Gated on the STUDENT'S OWN org rows (sign-in payload), not the
                    brand-wide organization.customizations — a student in an unflagged
                    org must not see College Apps because a sibling org has it. */}
                {user?.customizations?.includes('FillApp Enabled') && <>
                    <Route path='/college-apps' element={<FillApp />} />
                    <Route path='/college-apps/essays' element={<Essays />} />
                    <Route path='/college-apps/essays/:essayId' element={<EssayEditor />} />
                    <Route path='/college-apps/:screenId/:subtabId' element={<FillAppSection />} />
                </>}
                <Route path='/sign-in' element={<Navigate to={state?.referrer
                    ? state.referrer
                    : '/'
                } replace />} />
                {/* Self-signup lands here: SignUp creates the Firebase user without
                    navigating, Onboarding posts the profile and dispatches signIn, and
                    the app flips to PrivateRoutes while the URL is still /sign-up. */}
                <Route path='/sign-up' element={<Navigate to='/' replace />} />
                <Route path='/onboarding' element={<Navigate to='/' replace />} />
                <Route path='*' element={<NotFound />} />
            </Routes>
        </Suspense>
    );
}

export default PrivateRoutes;